/**
 * IndexedDB storage utility for Practido Code Workspace project drafts.
 * Replaces localStorage for large binary/base64 payloads (.sb3, audio, images).
 * Capacity: 500 MB+ (vs 5 MB in localStorage), preventing QuotaExceededError crashes.
 */

const DB_NAME = "practido_workspace_db";
const DB_VERSION = 1;
const STORE_NAME = "drafts";

let dbInstancePromise = null;
const memoryFallbackMap = new Map();

function getDB() {
  if (dbInstancePromise) return dbInstancePromise;

  dbInstancePromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      return reject(new Error("IndexedDB is not available in this environment."));
    }

    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "draftKey" });
        }
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        // Handle sudden closes/version changes
        db.onversionchange = () => {
          db.close();
          dbInstancePromise = null;
        };
        resolve(db);
      };

      request.onerror = (event) => {
        console.warn("Failed to open IndexedDB (falling back to in-memory session cache):", event.target.error);
        dbInstancePromise = null;
        reject(event.target.error);
      };
    } catch (e) {
      console.warn("IndexedDB.open threw exception (falling back to in-memory session cache):", e);
      dbInstancePromise = null;
      reject(e);
    }
  });

  return dbInstancePromise;
}

/**
 * Saves project draft payload to IndexedDB with in-memory session fallback.
 */
export async function saveDraftToIndexedDB(draftKey, draftData) {
  const record = {
    draftKey,
    ...draftData,
    updatedAt: Date.now(),
  };

  // Always maintain in-memory copy for instant retrieval and incognito fallback
  memoryFallbackMap.set(draftKey, record);

  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(record);

      req.onsuccess = () => resolve(true);
      req.onerror = (e) => {
        console.warn("IndexedDB put draft error (in-memory cached retained):", e.target.error);
        resolve(true);
      };
    });
  } catch (err) {
    // In strict incognito or disabled IndexedDB, the in-memory cache succeeds
    return true;
  }
}

/**
 * Retrieves project draft payload from IndexedDB with in-memory fallback.
 */
export async function getDraftFromIndexedDB(draftKey) {
  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(draftKey);

      req.onsuccess = () => resolve(req.result || memoryFallbackMap.get(draftKey) || null);
      req.onerror = (e) => {
        console.warn("IndexedDB get draft error, checking in-memory cache:", e.target.error);
        resolve(memoryFallbackMap.get(draftKey) || null);
      };
    });
  } catch (err) {
    return memoryFallbackMap.get(draftKey) || null;
  }
}

/**
 * Deletes project draft from IndexedDB and in-memory cache.
 */
export async function deleteDraftFromIndexedDB(draftKey) {
  memoryFallbackMap.delete(draftKey);
  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(draftKey);

      req.onsuccess = () => resolve(true);
      req.onerror = (e) => {
        console.warn("IndexedDB delete draft error:", e.target.error);
        resolve(true);
      };
    });
  } catch (err) {
    return true;
  }
}
